import { useState, useRef, useEffect } from 'react';
import { COUNTRIES, type Country } from '../../lib/countries';

interface Props {
  value: string;
  onChange: (country: Country) => void;
  placeholder?: string;
}

export default function CountrySelect({ value, onChange, placeholder = 'Search for a country...' }: Props) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = COUNTRIES.find((c) => c.name === value);

  // When not focused/open, show the selected country name
  const displayValue = open ? inputValue : (selected ? selected.name : '');

  const filtered = inputValue.trim()
    ? COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        c.code.toLowerCase().includes(inputValue.toLowerCase())
      )
    : COUNTRIES;

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightIndex(0);
  }, [inputValue]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-country-item]');
      if (items[highlightIndex]) {
        items[highlightIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightIndex, open]);

  function handleFocus() {
    setOpen(true);
    setInputValue('');
  }

  function selectCountry(country: Country) {
    onChange(country);
    setOpen(false);
    setInputValue('');
    inputRef.current?.blur();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        setInputValue('');
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[highlightIndex]) {
          selectCountry(filtered[highlightIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        setInputValue('');
        break;
    }
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        {!open && selected && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg leading-none pointer-events-none">
            {selected.flag}
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            !open && selected ? 'pl-10' : ''
          }`}
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-64 overflow-y-auto" ref={listRef}>
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">No countries found</div>
          ) : (
            filtered.map((country, index) => (
              <button
                key={country.code}
                type="button"
                data-country-item
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectCountry(country);
                }}
                onMouseEnter={() => setHighlightIndex(index)}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition ${
                  index === highlightIndex
                    ? 'bg-indigo-600 text-white'
                    : value === country.name
                      ? 'bg-indigo-900/40 text-indigo-300'
                      : 'text-gray-50 hover:bg-gray-700'
                }`}
              >
                <span className="text-lg leading-none">{country.flag}</span>
                <span>{country.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
